import React, { useEffect, useState, useCallback } from "react";
import { Plus, X, Trash2, RefreshCw, Receipt, Download, Pencil } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const API = process.env.REACT_APP_API_URL || "http://localhost:5000/api";
const BE  = process.env.REACT_APP_BACKEND_URL || "";

const CATEGORY_SUGGESTIONS = ["Utilities", "Maintenance", "Supplies", "Staff Salary", "Marketing", "Groceries", "Laundry", "Other"];
const PAYMENT_METHODS = ["Cash", "Card", "UPI", "Bank Transfer", "Other"];

function imgSrc(url) {
  if (!url) return null;
  return url.startsWith("http") ? url : `${BE}${url}`;
}

function ExpenseModal({ expense, onClose, onSaved, authHeaders }) {
  const isEdit = !!expense;
  const [form, setForm] = useState({
    date: expense?.date ? expense.date.slice(0, 10) : new Date().toISOString().slice(0, 10),
    category: expense?.category || "",
    amount: expense?.amount ?? "",
    paymentMethod: expense?.paymentMethod || "Cash",
    vendor: expense?.vendor || "",
    notes: expense?.notes || "",
  });
  const [receipt, setReceipt] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  async function submit() {
    if (!form.date || !form.category || !form.amount) { setError("Date, category and amount are required."); return; }
    setSaving(true); setError("");
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      if (receipt) fd.append("receipt", receipt);

      const res = await fetch(`${API}/expenses${isEdit ? `/${expense._id}` : ""}`, {
        method: isEdit ? "PATCH" : "POST",
        headers: authHeaders(),
        body: fd,
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.error);
      onSaved(json.data); onClose();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/30 backdrop-blur-sm">
      <div className="bg-white/80 backdrop-blur-2xl border border-white/50 rounded-2xl shadow-2xl shadow-black/10 w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
          <h2 className="text-lg font-bold text-gray-900">{isEdit ? "Edit Expense" : "New Expense"}</h2>
          <button onClick={onClose} className="p-1.5 -m-1.5"><X size={18} className="text-gray-500" /></button>
        </div>
        <div className="px-6 py-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Date *</label>
              <input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))}
                className="w-full border border-gray-200 rounded-xl px-3 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Amount (₹) *</label>
              <input type="number" min="0" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))}
                className="w-full border border-gray-200 rounded-xl px-3 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Category *</label>
            <input list="expense-categories" value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
              placeholder="e.g. Utilities"
              className="w-full border border-gray-200 rounded-xl px-3 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" />
            <datalist id="expense-categories">
              {CATEGORY_SUGGESTIONS.map(c => <option key={c} value={c} />)}
            </datalist>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Payment Method</label>
              <select value={form.paymentMethod} onChange={e => setForm(f => ({ ...f, paymentMethod: e.target.value }))}
                className="w-full border border-gray-200 rounded-xl px-3 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 bg-white">
                {PAYMENT_METHODS.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Vendor</label>
              <input value={form.vendor} onChange={e => setForm(f => ({ ...f, vendor: e.target.value }))}
                className="w-full border border-gray-200 rounded-xl px-3 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Notes</label>
            <textarea rows={2} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              className="w-full border border-gray-200 rounded-xl px-3 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Receipt</label>
            {expense?.receiptUrl && !receipt && (
              <a href={imgSrc(expense.receiptUrl)} target="_blank" rel="noopener noreferrer" className="text-xs text-amber-600 underline block mb-1.5">
                View current receipt
              </a>
            )}
            <input type="file" accept="image/*" onChange={e => setReceipt(e.target.files?.[0] || null)}
              className="w-full text-sm text-gray-600 file:mr-3 file:py-2 file:px-4 file:rounded-xl file:border-0
                file:bg-amber-50 file:text-amber-700 file:text-sm file:font-medium hover:file:bg-amber-100" />
          </div>
          {error && <p className="text-sm text-red-600 bg-red-50 rounded-xl px-3 py-2">{error}</p>}
        </div>
        <div className="px-6 py-4 border-t border-gray-100">
          <button onClick={submit} disabled={saving}
            className="w-full py-3.5 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 text-white
              font-semibold text-sm shadow-sm hover:from-amber-600 hover:to-orange-600 disabled:opacity-60 transition-all">
            {saving ? "Saving…" : isEdit ? "Save Changes" : "Add Expense"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ExpensesPage() {
  const { authHeaders } = useAuth();
  const [expenses, setExpenses] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [category, setCategory] = useState("");
  const [modalFor, setModalFor] = useState(null); // null = closed, "new" = create, or an expense object to edit

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (from) params.set("from", from);
      if (to) params.set("to", to);
      if (category) params.set("category", category);
      const res = await fetch(`${API}/expenses?${params}`, { headers: authHeaders() });
      const json = await res.json();
      setExpenses(json.data || []);
      setTotal(json.total || 0);
    } catch {}
    setLoading(false);
    // eslint-disable-next-line
  }, [from, to, category]);

  useEffect(() => { load(); }, [load]);

  async function remove(id) {
    if (!window.confirm("Delete this expense?")) return;
    const res = await fetch(`${API}/expenses/${id}`, { method: "DELETE", headers: authHeaders() });
    if (res.ok) { setExpenses(prev => prev.filter(e => e._id !== id)); load(); }
  }

  function exportCsv() {
    fetch(`${API}/expenses/csv`, { headers: authHeaders() })
      .then(r => r.blob())
      .then(blob => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = `expenses-${Date.now()}.csv`;
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(url);
      });
  }

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Expenses</h1>
          <p className="text-sm text-gray-500">{expenses.length} expense{expenses.length !== 1 ? "s" : ""} · ₹{total.toLocaleString("en-IN")} total</p>
        </div>
        <div className="flex items-center gap-3 sm:gap-2">
          <button onClick={load} className="tap-44 p-2 hover:bg-gray-100 rounded-xl transition-colors">
            <RefreshCw size={16} className={`text-gray-500 ${loading ? "animate-spin" : ""}`} />
          </button>
          <button onClick={exportCsv} className="tap-44 flex items-center gap-1.5 p-2.5 sm:px-4 sm:py-2 rounded-xl bg-gray-100 text-gray-700 text-sm font-medium hover:bg-gray-200 transition-colors">
            <Download size={15} /> <span className="hidden sm:inline">Export CSV</span>
          </button>
          <button onClick={() => setModalFor("new")}
            className="tap-44 flex items-center gap-2 text-sm font-medium text-white
              bg-gradient-to-r from-amber-500 to-orange-500 px-4 py-2 rounded-xl
              shadow-sm hover:from-amber-600 hover:to-orange-600 transition-all">
            <Plus size={15} /> New Expense
          </button>
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap bg-white rounded-2xl border border-gray-100 p-3">
        <input type="date" value={from} onChange={e => setFrom(e.target.value)}
          className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" />
        <span className="text-gray-400 text-sm">to</span>
        <input type="date" value={to} onChange={e => setTo(e.target.value)}
          className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" />
        <input value={category} onChange={e => setCategory(e.target.value)} placeholder="Filter by category"
          className="border border-gray-200 rounded-xl px-3 py-2 text-sm flex-1 min-w-[140px] focus:outline-none focus:ring-2 focus:ring-amber-400" />
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="hidden md:block overflow-x-auto">
          <table className="text-xs w-full">
            <thead>
              <tr className="border-b border-gray-100">
                {["Date","Category","Amount","Method","Vendor","Receipt","Actions"].map(h => (
                  <th key={h} className="text-left text-[10.5px] font-semibold text-gray-400 uppercase tracking-wider px-3 py-2 whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {loading ? (
                <tr><td colSpan={7} className="text-center py-12 text-gray-400"><RefreshCw size={20} className="animate-spin mx-auto mb-2" /> Loading…</td></tr>
              ) : expenses.length === 0 ? (
                <tr><td colSpan={7} className="text-center py-12 text-gray-400">No expenses recorded yet</td></tr>
              ) : expenses.map(e => (
                <tr key={e._id} className="hover:bg-gray-50/50 transition-colors">
                  <td className="px-3 py-2 text-gray-700 whitespace-nowrap">{new Date(e.date).toLocaleDateString("en-IN")}</td>
                  <td className="px-3 py-2">
                    <span className="font-medium text-gray-900">{e.category}</span>
                    {e.notes && <p className="text-[10.5px] text-gray-400">{e.notes}</p>}
                  </td>
                  <td className="px-3 py-2 font-semibold text-gray-900">₹{e.amount.toLocaleString("en-IN")}</td>
                  <td className="px-3 py-2 text-gray-500">{e.paymentMethod}</td>
                  <td className="px-3 py-2 text-gray-500">{e.vendor || "—"}</td>
                  <td className="px-3 py-2">
                    {e.receiptUrl ? (
                      <a href={imgSrc(e.receiptUrl)} target="_blank" rel="noopener noreferrer" className="text-amber-600 underline flex items-center gap-1">
                        <Receipt size={12} /> View
                      </a>
                    ) : "—"}
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-1.5">
                      <button onClick={() => setModalFor(e)} className="tap-44 p-1.5 rounded-lg bg-amber-50 text-amber-600 hover:bg-amber-100">
                        <Pencil size={12} />
                      </button>
                      <button onClick={() => remove(e._id)} className="tap-44 p-1.5 rounded-lg bg-red-50 text-red-500 hover:bg-red-100">
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="md:hidden divide-y divide-gray-100">
          {loading ? (
            <div className="text-center py-12 text-gray-400"><RefreshCw size={20} className="animate-spin mx-auto mb-2" /> Loading…</div>
          ) : expenses.length === 0 ? (
            <p className="text-center py-12 text-gray-400">No expenses recorded yet</p>
          ) : (
            expenses.map(e => (
              <div key={e._id} className="p-3.5 space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-semibold text-gray-900">{e.category}</p>
                    <p className="text-[10.5px] text-gray-400">{new Date(e.date).toLocaleDateString("en-IN")} · {e.paymentMethod}</p>
                  </div>
                  {/* gap-5, not gap-1.5: .tap-44 grows only the invisible hit
                      area, so adjacent icons need ~44px of pitch or their two
                      areas overlap and each keeps barely more than its own width. */}
                  <div className="flex items-center gap-5 flex-shrink-0">
                    <button onClick={() => setModalFor(e)} className="tap-44 p-2 rounded-lg bg-amber-50 text-amber-600">
                      <Pencil size={14} />
                    </button>
                    <button onClick={() => remove(e._id)} className="tap-44 p-2 rounded-lg bg-red-50 text-red-500">
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
                <p className="text-lg font-bold text-gray-900">₹{e.amount.toLocaleString("en-IN")}</p>
                {e.vendor && <p className="text-xs text-gray-500">{e.vendor}</p>}
                {e.receiptUrl && (
                  <a href={imgSrc(e.receiptUrl)} target="_blank" rel="noopener noreferrer" className="text-xs text-amber-600 underline flex items-center gap-1">
                    <Receipt size={12} /> View receipt
                  </a>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      {modalFor && (
        <ExpenseModal
          expense={modalFor === "new" ? null : modalFor}
          onClose={() => setModalFor(null)}
          onSaved={saved => setExpenses(prev => modalFor === "new" ? [saved, ...prev] : prev.map(e => e._id === saved._id ? saved : e))}
          authHeaders={authHeaders}
        />
      )}
    </div>
  );
}
